import { useCallback } from 'react'
import {
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query'
import { type CurrentUserResponse, useGetUserCurrent } from '../generated'
import { type ErrorType, useMutator } from '../useMutator'

type DeleteUserBody = {
  password?: string
}

type DeleteUserInput = { data?: DeleteUserBody | undefined } | undefined

type Credentials = {
  email: string
  password: string
}

type SignUpBody = Credentials & {
  name: {
    firstName: string
    lastName: string
  }
  marketingOptIn: boolean
  verificationPath: string
  captcha: string | null
}

type SignUpInput = { data: SignUpBody }

type UpdateAccountNewPasswordBody = {
  email: string
  currentPassword: string
  newPassword: string
}

type UpdateAccountNewPasswordInput = { data: UpdateAccountNewPasswordBody }

export const useDeleteUser = <TError = unknown, TContext = unknown>(options?: {
  language: string
  mutation?: UseMutationOptions<unknown, TError, DeleteUserInput, TContext>
}) => {
  const deleteUser = useMutator()

  return useMutation({
    mutationFn: async ({ data }: DeleteUserInput = {}) => {
      const password = data?.password ?? 'NOT_PROVIDED'
      return deleteUser({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        url: `/user/initiate-delete?x-lang=${options?.language}`,
        data: { password },
      })
    },
    ...options?.mutation,
  })
}

export const useSignUpUser = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<unknown, TError, SignUpInput, TContext>
}) => {
  const signUp = useMutator()

  return useMutation({
    mutationFn: async ({ data }: SignUpInput) =>
      signUp({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        url: `/user/register?sendAlreadyKnownEmail=true&sendVerificationEmail=true&verificationPath=${data.verificationPath}`,
        data,
      }),
    ...options?.mutation,
  })
}

export const useUpdateAccountPassword = () => {
  const updateAccountPassword = useMutator()

  return useMutation({
    mutationFn: ({ data }: UpdateAccountNewPasswordInput) =>
      updateAccountPassword({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        url: '/v2/user/change-password-in-app',
        data,
      }),
  })
}

export const useIsSocialUserAccount = () => {
  const { data, isLoading } = useGetUserCurrent()

  if (isLoading) {
    return undefined
  }

  return ['google', 'apple'].includes(data?.user.origin ?? '')
}

const getCurrentUserAnonymousUrl = '/user/current-stable-anonymous'
type GetCurrentUserAnonymousResult = UseQueryResult<CurrentUserResponse, ErrorType<Error>> & { queryKey: QueryKey }
type GetCurrentUserAnonumousOptions = {
  query?: Partial<UseQueryOptions<CurrentUserResponse, ErrorType<Error>>>
}

const useGetUserCurrentAnonymousHook = () => {
  const getUserCurrentAnonymous = useMutator<CurrentUserResponse>()
  return useCallback(
    (signal?: AbortSignal) =>
      getUserCurrentAnonymous({
        url: getCurrentUserAnonymousUrl,
        method: 'GET',
        signal,
      }),
    [getUserCurrentAnonymous]
  )
}

export const useGetUserCurrentAnonymous = (queryOptions?: GetCurrentUserAnonumousOptions) => {
  const queryFn = useGetUserCurrentAnonymousHook()
  const queryKey = [getCurrentUserAnonymousUrl, queryFn]
  const query = useQuery({
    ...queryOptions?.query,
    queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => queryFn(signal),
  }) as GetCurrentUserAnonymousResult
  query.queryKey = queryKey as QueryKey
  return query
}

// This hook is used to refresh the user data every 25 minutes (by default)
// It uses a seperate querykey to avoid conflicts with the initial query
export const useGetUserCurrentAnonymousRefresh = (queryOptions?: GetCurrentUserAnonumousOptions) => {
  const queryFn = useGetUserCurrentAnonymousHook()
  const queryKey = ['refresh', getCurrentUserAnonymousUrl, queryFn]
  const query = useQuery({
    refetchInterval: 1000 * 60 * 10, // 10 minutes refresh. The token expires after 30 minutes
    refetchIntervalInBackground: true,
    ...queryOptions?.query,
    queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => queryFn(signal),
  }) as GetCurrentUserAnonymousResult
  query.queryKey = queryKey as QueryKey
  return query
}
